import { lazy, Suspense, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Home, Monitor, Users, ExternalLink, AlertTriangle, Server, Shield, Database, Laptop, MessageSquare, Wrench, Lightbulb, Phone, Link2, Cloud, Clock, BookOpen, User, HelpCircle, Building2, Stethoscope, Scale, Briefcase, MapPin, Award, Crown, Calculator, Target, HandshakeIcon, ShieldCheck, HeadphonesIcon, Wallet, Settings, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/LanguageSelector';
import { CalendlyModal } from '@/components/CalendlyModal';
import { DeferredMegaMenu } from '@/components/DeferredMegaMenu';
import { useLanguage } from '@/i18n/LanguageContext';
import { menuTranslations } from '@/i18n/menuTranslations';
import { useHashNavigation } from '@/hooks/useHashNavigation';
import logoDark from '@/assets/logo-consultic-new.png';
import { cn } from '@/lib/utils';

// Solution categories will be generated inside the component with translations

const loadSolutionsMegaMenu = () => import("@/components/SolutionsMegaMenu");
const LazySolutionsMegaMenu = lazy(() => loadSolutionsMegaMenu().then(({ SolutionsMegaMenu }) => ({ default: SolutionsMegaMenu })));
const loadProblemsMegaMenu = () => import("@/components/ProblemsMegaMenu");
const LazyProblemsMegaMenu = lazy(() => loadProblemsMegaMenu().then(({ ProblemsMegaMenu }) => ({ default: ProblemsMegaMenu })));
const loadRolesMegaMenu = () => import("@/components/RolesMegaMenu");
const LazyRolesMegaMenu = lazy(() => loadRolesMegaMenu().then(({ RolesMegaMenu }) => ({ default: RolesMegaMenu })));
const loadSectorsMegaMenu = () => import("@/components/SectorsMegaMenu");
const LazySectorsMegaMenu = lazy(() => loadSectorsMegaMenu().then(({ SectorsMegaMenu }) => ({ default: SectorsMegaMenu })));
const loadAboutMegaMenu = () => import("@/components/AboutMegaMenu");
const LazyAboutMegaMenu = lazy(() => loadAboutMegaMenu().then(({ AboutMegaMenu }) => ({ default: AboutMegaMenu })));
const loadHelpAccessMegaMenu = () => import("@/components/HelpAccessMegaMenu");
const LazyHelpAccessMegaMenu = lazy(() => loadHelpAccessMegaMenu().then(({ HelpAccessMegaMenu }) => ({ default: HelpAccessMegaMenu })));
const MobileNavigation = lazy(() => import("@/components/MobileNavigation").then(({ MobileNavigation }) => ({ default: MobileNavigation })));

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<string | null>(null);
  const { t, language } = useLanguage();
  const menuT = menuTranslations[language];
  const { handleNavigation } = useHashNavigation();

  // Define solution categories with translations
  const solutionCategories = {
    infrastructure: {
      label: t.solutionsMenu.infrastructure,
      items: [
        { name: t.submenu.hosting, href: '/services/hosting', icon: Server, color: '#0052CC' },
        { name: t.submenu.security, href: '/services/security', icon: Shield, color: '#EF4444' },
        { name: t.submenu.backup, href: '/services/backup', icon: Database, color: '#10B981' },
      ]
    },
    productivity: {
      label: t.solutionsMenu.productivity,
      items: [
        { name: t.submenu.mobility, href: '/services/mobility', icon: Laptop, color: '#7C3AED' },
        { name: t.submenu.communication, href: '/services/communication', icon: MessageSquare, color: '#06B6D4' },
        { name: t.submenu.hardware, href: '/services/hardware', icon: Wrench, color: '#64748B' },
      ]
    },
    services: {
      label: t.solutionsMenu.services,
      items: [
        { name: t.submenu.support, href: '/services/support', icon: AlertTriangle, color: '#F59E0B' },
        { name: t.submenu.consulting, href: '/services/consulting', icon: Lightbulb, color: '#FBBF24' },
      ]
    }
  };

  // Define about categories with translations
  const aboutCategories = {
    discover: {
      label: t.aboutMenu.sectionDiscover,
      items: [
        { name: t.aboutMenu.history, href: '/a-propos/histoire' },
        { name: t.aboutMenu.team, href: '/a-propos/equipe' },
        { name: t.aboutMenu.values, href: '/a-propos/valeurs' },
      ]
    },
    other: [
      { name: t.aboutMenu.articles, href: '/a-propos/articles' },
      { name: t.aboutMenu.press, href: '/a-propos/presse' },
      { name: t.aboutMenu.testimonials, href: '/a-propos/temoignages' },
      { name: t.aboutMenu.technologies, href: '/a-propos/technologies' },
      { name: t.aboutMenu.career, href: '/recrutement' },
    ]
  };

  // Help & Access items with translations
  const helpAccessItems = [
    { name: t.helpMenu.portalConsultic, href: '/portail/client', icon: Wrench, description: t.helpMenu.portalConsulticDesc },
    { name: t.helpMenu.skyCorporate, href: '/portail/skycorporate', icon: Monitor, description: t.helpMenu.skyCorporateDesc, badge: 'PRO' },
    { name: t.helpMenu.microsoft365, href: 'https://office.com/', icon: Cloud, description: t.helpMenu.microsoft365Desc, isExternal: true },
    { name: 'TeamViewer', href: 'https://get.teamviewer.com/consultic', icon: Link2, description: t.helpMenu.remoteAssistance, isExternal: true },
    { name: 'AnyDesk', href: 'https://anydesk.com/download', icon: Link2, description: t.helpMenu.remoteAssistance, isExternal: true },
    { name: t.helpMenu.callSupport, href: 'tel:+3228807070', icon: Phone, description: '+32 2 880 70 70', isUrgent: true },
  ];

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  }, []);

  

  const NavLink = ({ href, children, className, onClick }: { href: string; children: React.ReactNode; className?: string; onClick?: () => void }) => {
    const isHashLink = href.includes('#');
    const isExternalLink = href.startsWith('http') || href.startsWith('tel:') || href.startsWith('mailto:');

    if (isExternalLink) {
      return (
        <a href={href} className={className} target={href.startsWith('http') ? "_blank" : undefined} rel={href.startsWith('http') ? "noopener noreferrer" : undefined} onClick={onClick}>
          {children}
        </a>
      );
    }

    if (isHashLink) {
      return (
        <button
          type="button"
          className={className}
          onClick={(e) => {
            e.preventDefault();
            handleNavigation(href, onClick);
          }}
        >
          {children}
        </button>
      );
    }

    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  };

  const toggleMobileAccordion = (key: string) => {
    setOpenMobileAccordion(openMobileAccordion === key ? null : key);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        isScrolled ? "py-3" : "py-5",
      )}
    >
      {/* Premium Dark Background (constant to avoid color shift on scroll) */}
      <div
        className={cn(
          "absolute inset-0 transition-all duration-500",
          "bg-[hsl(var(--header-bg-solid))]/95 dark:bg-[hsl(var(--header-bg-solid))]/95 backdrop-blur-2xl",
          "shadow-[0_4px_60px_hsl(var(--secondary)/0.15)]",
        )}
      />

      {/* Animated gradient border at bottom */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-500 overflow-hidden",
          isScrolled ? "opacity-100" : "opacity-60",
        )}
      >
        <div
          className="h-full w-full"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, hsl(var(--secondary)) 20%, hsl(var(--accent)) 50%, hsl(var(--secondary)) 80%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer-border 8s linear infinite",
            willChange: "transform",
          }}
        />
      </div>

      <style>{`
        @keyframes shimmer-border {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo with glow effect */}
          <Link 
            to="/" 
            className="flex items-center group relative"
            aria-label={t.accessibility.home}
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-secondary/20 to-accent/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-xl" />
            <img 
              src={logoDark} 
              alt="ConsulTIC - Cloud & IT Solutions" 
              width={172}
              height={36}
              fetchPriority="high"
              decoding="sync"
              className="h-9 sm:h-10 md:h-12 w-auto relative transition-all duration-300 group-hover:scale-105 drop-shadow-[0_0_20px_rgba(0,158,224,0.3)]"
            />
          </Link>

          {/* Desktop Navigation - New Order: Nos solutions, Votre besoin, Votre profil, Secteurs, À propos, Services et Support, Contact */}
          <div className="hidden xl:flex items-center gap-5">
            {/* 1. Nos solutions (ex-Votre besoin) */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label={menuT.nav.solutions} fallbackHref="/services" load={loadSolutionsMegaMenu}>
                {(initialOpen) => <LazySolutionsMegaMenu label={menuT.nav.solutions} initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 2. Votre besoin */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label={menuT.nav.problems} fallbackHref="/problematiques" load={loadProblemsMegaMenu}>
                {(initialOpen) => <LazyProblemsMegaMenu label={menuT.nav.problems} initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 3. Votre profil */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label={menuT.nav.forWhom} fallbackHref="/pour-qui" load={loadRolesMegaMenu}>
                {(initialOpen) => <LazyRolesMegaMenu label={menuT.nav.forWhom} initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 4. Secteurs */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label={menuT.nav.sectors} fallbackHref="/secteurs" load={loadSectorsMegaMenu}>
                {(initialOpen) => <LazySectorsMegaMenu label={menuT.nav.sectors} initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 5. À propos */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label={t.nav.about} fallbackHref="/a-propos/histoire" load={loadAboutMegaMenu}>
                {(initialOpen) => <LazyAboutMegaMenu label={t.nav.about} initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 6. Support (avec icône casque) */}
            <div className="relative [&_button]:text-white/80 [&_button]:hover:text-white [&_button]:font-medium [&_button]:text-[15px]">
              <DeferredMegaMenu label="Support" fallbackHref="/support/demande" load={loadHelpAccessMegaMenu}>
                {(initialOpen) => <LazyHelpAccessMegaMenu initialOpen={initialOpen} />}
              </DeferredMegaMenu>
            </div>

            {/* 7. Contact */}
            <Link 
              to="/contact" 
              className="relative text-white/80 hover:text-white font-medium text-[15px] transition-all duration-300 group py-2"
            >
              <span>{t.nav.contact}</span>
              <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-secondary to-accent group-hover:w-full transition-all duration-300" />
            </Link>

            {/* 8. Portail Client (icône bonhomme) */}
            <Link
              to="/support/portail-client"
              className="relative text-white/80 hover:text-secondary transition-all duration-300 p-2 rounded-lg hover:bg-white/5"
              title={t.helpMenu.portalConsultic}
            >
              <User className="w-5 h-5" />
            </Link>
          </div>

          {/* Language Selector */}
          <div className="hidden xl:flex items-center">
            <div className="[&_button]:text-white/70 [&_button]:hover:text-white [&_button]:border-white/20 [&_button]:hover:border-white/40">
              <LanguageSelector />
            </div>
          </div>

          {/* Mobile Menu Button - Premium Dark */}
          <div className="xl:hidden flex items-center gap-3">
            <div className="[&_button]:text-white/70 [&_button]:border-white/20">
              <LanguageSelector compact />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn(
                "p-3 rounded-xl transition-all duration-300 border",
                isMobileMenuOpen 
                  ? "bg-secondary border-secondary text-white shadow-[0_0_20px_rgba(0,158,224,0.5)]" 
                  : "bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/40"
              )}
              aria-expanded={isMobileMenuOpen}
              aria-label={t.accessibility.mainMenu}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <Suspense fallback={<div className="border-t border-white/10 bg-[hsl(var(--header-bg-solid))]" aria-busy="true" />}>
            <MobileNavigation
              t={t}
              menuT={menuT}
              solutionCategories={solutionCategories}
              aboutCategories={aboutCategories}
              helpAccessItems={helpAccessItems}
              openMobileAccordion={openMobileAccordion}
              toggleMobileAccordion={toggleMobileAccordion}
              closeMobileMenu={closeMobileMenu}
              onBookMeeting={() => setIsCalendlyOpen(true)}
            />
          </Suspense>
        )}
      </nav>

      <CalendlyModal 
        isOpen={isCalendlyOpen} 
        onClose={() => setIsCalendlyOpen(false)} 
      />
    </header>
  );
}
